import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { axiosClient } from '../axiosClient';

import { ProjectResponseType } from '../../types/apiTypes';
import {
  getAdsFilterParamsFromApiData,
  getCoordinatesLatLng,
  getSoldFilterParamsFromApiData,
} from '../../helpers';
import {
  AdsFilterDataType,
  AdsRefDataType,
  AdsWeightDataType,
  CoordinatesType,
  SoldFilterDataType,
  SoldRefDataType,
  SoldWeightDataType,
  setCurrentProjectType,
} from '../../types/dataTypes';

interface SalesRefResponse {
  soldParams: {
    refData: Partial<SoldRefDataType>;
    weightData: Partial<SoldWeightDataType>;
    filterData: Partial<SoldFilterDataType>;
  };
  adsParams: {
    refData: Partial<AdsRefDataType>;
    weightData: Partial<AdsWeightDataType>;
    filterData: Partial<AdsFilterDataType>;
  };
  project: setCurrentProjectType;
  coordinates: CoordinatesType;
}

const useCreateProject = (
  options?: UseMutationOptions<SalesRefResponse, Error, void>
) => {
  return useMutation({
    mutationFn: async (): Promise<SalesRefResponse> => {
      const URL = `${import.meta.env.VITE_API_PROJECTS_URL}/new`;

      const { data } = await axiosClient.get<ProjectResponseType>(URL);

      const {
        id,
        name,
        ad_references_search_criteria: adsSearchCritera,
        sale_references_search_criteria: salesSearchCritera,
        index_normalized_search_criteria: indexValues,
      } = data.project;

      const { soldRefValues, soldWeightValues, soldFilterValues } =
        getSoldFilterParamsFromApiData(salesSearchCritera);
      const { adsRefValues, adsWeightValues, adsFilterValues } =
        getAdsFilterParamsFromApiData(adsSearchCritera);

      const coordinates = getCoordinatesLatLng(
        salesSearchCritera?.wgs84lat,
        salesSearchCritera?.wgs84lon
      );

      const soldParams = {
        refData: soldRefValues,
        weightData: soldWeightValues,
        filterData: soldFilterValues,
      };
      const adsParams = {
        refData: adsRefValues,
        weightData: adsWeightValues,
        filterData: adsFilterValues,
      };

      const project = {
        id,
        name,
        excludedSoldIds: salesSearchCritera?.selected_ids ?? [],
        selectedSoldIds: salesSearchCritera?.selected_ids ?? [],
        excludedAdsIds: adsSearchCritera?.excluded_ids ?? [],
        selectedAdsIds: adsSearchCritera?.selected_ids ?? [],
        indexValues,
      };

      return { soldParams, adsParams, project, coordinates };
    },
    ...options,
  });
};

export default useCreateProject;
